import { XIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { StafferPetProfileSprite } from "@/components/staffer-pets/StafferPetProfileSprite";
import { Button } from "@ui/button";

import { useGetWeeklyCoinReport } from "@/hooks/queries/useGetWeeklyCoinReport";
import { stafferPets } from "@/lib/stafferPets";
import { Pet, User } from "@/types";

const CARD_SPRITE_SIZE_PX = 64;

// Summary card for the pet that was just clicked in the yard.
export const PlayyardPetCard = ({
  user,
  rank,
  rankedCount,
  onClose,
}: {
  user: User & { pet: Pet };
  rank: number | null;
  rankedCount: number;
  onClose: () => void;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: weeklyReport } = useGetWeeklyCoinReport("THIS_WEEK");

  /* Helpers */
  const { petId, colorId } = user.pet.equipped;
  const palette = stafferPets.getPalette(petId, colorId);
  const weeklyCoins = weeklyReport?.staffers.find(
    (staffer) => staffer.userId === user.id,
  )?.coinsEarned;

  /* Render */
  return (
    <Column className="absolute right-3 top-3 z-[70] w-64 gap-2 rounded-xl border-2 border-white bg-[#fff8ec]/95 p-3 text-[#3a2a1c] shadow-[0_10px_30px_rgb(60_30_10_/_0.35)]">
      <Button
        type="button"
        variant="ghost"
        onClick={onClose}
        className="absolute right-1.5 top-1.5 h-6 w-6 p-0 text-[#3a2a1c]/60 hover:bg-transparent hover:text-[#3a2a1c]"
      >
        <XIcon className="h-4 w-4" />
        <Text className="sr-only">Close</Text>
      </Button>
      <Row className="items-center gap-3">
        <Column className="h-[72px] w-[72px] shrink-0 items-center justify-center rounded-xl bg-[#f6e3c4]">
          <StafferPetProfileSprite
            pet={user.pet}
            sizePx={CARD_SPRITE_SIZE_PX}
          />
        </Column>
        <Column className="min-w-0 gap-0.5">
          <Text className="truncate text-base font-bold leading-tight text-[#3a2a1c]">
            {user.firstName} {user.lastName}
          </Text>
          <Text className="text-xs font-medium capitalize text-[#3a2a1c]/75">
            {palette.label} {stafferPets.getSpeciesLabel(petId).toLowerCase()}
          </Text>
          <Text className="w-fit rounded-full bg-[#3a2a1c] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#ffd37c]">
            {palette.rarity}
          </Text>
        </Column>
      </Row>
      <Column className="gap-1">
        <PetCardStat
          label="Coin balance"
          value={user.pet.coinBalance.toLocaleString("en-US")}
        />
        <PetCardStat
          label="Earned this week"
          value={
            weeklyCoins === undefined
              ? "—"
              : `+${weeklyCoins.toLocaleString("en-US")}`
          }
        />
        {rank === null ? null : (
          <PetCardStat
            label="Coin rank"
            value={`#${rank.toString()} of ${rankedCount.toString()}`}
          />
        )}
      </Column>
    </Column>
  );
};

const PetCardStat = ({ label, value }: { label: string; value: string }) => {
  return (
    <Row className="items-center justify-between gap-3">
      <Text className="text-xs font-medium text-[#3a2a1c]/75">{label}</Text>
      <Text className="text-xs font-bold tabular-nums text-[#3a2a1c]">
        {value}
      </Text>
    </Row>
  );
};
